/* global fetchVenueContractorsIndexUrl */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import VenueContractorsByDayTable from './contractors/VenueContractorsByDayTable';
import VenueContractorsByFunctionalAreaTable from './contractors/VenueContractorsByFunctionalAreaTable';
import VenueContractorsByPackTable from './contractors/VenueContractorsByPackTable';
import VenueContractorsEmptyState from './contractors/VenueContractorsEmptyState';
import VenueContractorsTiles from './contractors/VenueContractorsTiles';

import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import SegmentedControl from 'CommonComponents/SegmentedControl';
import SelectInput from 'CommonComponents/SelectInput';

const VIEW_PACK = 'pack';
const VIEW_FUNCTIONAL_AREA = 'functional_area';
const VIEW_DAY = 'day';

function VenueContractors({ fetchUrl }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [functionalArea, setFunctionalArea] = useState('');
    const [view, setView] = useState(VIEW_PACK);
    const [expandedPacks, setExpandedPacks] = useState({});
    const [expandedDays, setExpandedDays] = useState({});
    const latestRequest = useRef(0);

    // Only the newest request may land: a quick change of functional area must not be
    // overwritten by the slower response to the previous one.
    useEffect(() => {
        const requestId = ++latestRequest.current;
        setLoading(true);
        setFailed(false);

        apiClient
            .get(fetchUrl, { params: functionalArea ? { functional_area: functionalArea } : {} })
            .then(({ data: response }) => {
                if (requestId === latestRequest.current) {
                    setData(response);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (requestId === latestRequest.current) {
                    setFailed(true);
                    setLoading(false);
                }
            });
    }, [functionalArea, attempt]);

    const toggle = (setter) => (key) => setter((prev) => ({ ...prev, [key]: !prev[key] }));

    if (failed) {
        return (
            <div className="table__card tw-flex tw-flex-wrap tw-items-center tw-gap-4" role="alert">
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('The contractor summary could not be loaded.')}</p>
                <Button variant="secondary" small onClick={() => setAttempt((prev) => prev + 1)}>
                    {$t('Try again')}
                </Button>
            </div>
        );
    }

    const isLoading = loading || !data;

    if (!isLoading && data.latest_estimate_only) {
        return (
            <div className="table__card">
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">
                    {$t('Contractor summary is available on the Latest Estimate. Switch to Latest Estimate to view it.')}
                </p>
            </div>
        );
    }

    const isEmpty = !isLoading && data.by_pack.length === 0 && !functionalArea;
    const options = data?.functional_area_options ?? [];

    return (
        <div className="tw-flex tw-flex-col tw-gap-6">
            {!isEmpty && (
                <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
                    <div className="tw-w-full sm:tw-w-72">
                        <SelectInput
                            name="venue-contractors-functional-area"
                            label={$t('Functional area')}
                            value={functionalArea}
                            onChange={(event) => setFunctionalArea(event.target.value)}
                        >
                            <option value="">{$t('All functional areas')}</option>
                            {options.flatMap((department) => [
                                <option key={`department-${department.id}`} value={`department-${department.id}`}>
                                    {department.name}
                                </option>,
                                ...department.costcenters.map((costcenter) => (
                                    // Native options ignore padding, so the indent is non-breaking spaces.
                                    <option key={`costcenter-${costcenter.id}`} value={`costcenter-${costcenter.id}`}>
                                        {`\u00A0\u00A0\u00A0\u00A0${costcenter.name}`}
                                    </option>
                                )),
                            ])}
                        </SelectInput>
                    </div>
                    <SegmentedControl
                        value={view}
                        onChange={setView}
                        options={[
                            { value: VIEW_PACK, label: $t('By pack') },
                            { value: VIEW_FUNCTIONAL_AREA, label: $t('By functional area') },
                            { value: VIEW_DAY, label: $t('By day') },
                        ]}
                    />
                </div>
            )}

            <VenueContractorsTiles tiles={data?.tiles} loading={isLoading} />

            <div className="table__card">
                {isEmpty ? (
                    <VenueContractorsEmptyState links={data.empty_state_links} />
                ) : (
                    <>
                        {view === VIEW_PACK && (
                            <>
                                <ViewHeading title={$t('Packs allocated to this venue')} hint={$t('Click a pack to see the roles inside it.')} />
                                <VenueContractorsByPackTable
                                    rows={data?.by_pack ?? []}
                                    totals={data?.by_pack_totals}
                                    loading={isLoading}
                                    expanded={expandedPacks}
                                    onToggle={toggle(setExpandedPacks)}
                                />
                            </>
                        )}
                        {view === VIEW_FUNCTIONAL_AREA && (
                            <>
                                <ViewHeading title={$t('Contractors by functional area')} />
                                <VenueContractorsByFunctionalAreaTable
                                    rows={data?.by_functional_area ?? []}
                                    totals={data?.by_functional_area_totals}
                                    loading={isLoading}
                                />
                            </>
                        )}
                        {view === VIEW_DAY && (
                            <>
                                <ViewHeading
                                    title={$t('Contractors by day')}
                                    hint={$t('One row per day with packs allocated. Click a day to see its packs.')}
                                />
                                <VenueContractorsByDayTable
                                    rows={data?.by_day ?? []}
                                    totals={data?.by_day_totals}
                                    loading={isLoading}
                                    expanded={expandedDays}
                                    onToggle={toggle(setExpandedDays)}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function ViewHeading({ title, hint }) {
    return (
        <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-mb-4">
            <h4 className="table__header !tw-mb-0">{title}</h4>
            {hint && <span className="tw-text-xs tw-text-charcoal-50">{hint}</span>}
        </div>
    );
}

if (document.getElementById('VenueContractors')) {
    ReactDOM.render(
        <App>
            <VenueContractors fetchUrl={fetchVenueContractorsIndexUrl} />
        </App>,
        document.getElementById('VenueContractors'),
    );
}
